/*
 * variant-mixed.js — Adorn Your Table
 * Custom element <variant-mixed>: renders the Size option as a native <select>
 * while every other option stays as radios / colour swatches.
 * Extends the theme's VariantRadios (global.js) so pricing, media,
 * availability, URL and sticky-cart behaviour are unchanged.
 * Loaded by snippets/product-variant-picker.liquid in "mixed" mode.
 *
 * v2 (Sept 2026): sizes that do not exist for the selected shape/colour are
 * disabled in the dropdown; if the current size becomes invalid the first valid
 * size is selected automatically. Sizes that exist but are sold out stay
 * selectable and are labelled "— Sold out".
 */
(function () {
  if (customElements.get('variant-mixed')) return;
  if (typeof VariantRadios === 'undefined') {
    console.warn('variant-mixed: VariantRadios is not defined (global.js must load first).');
    return;
  }

  var SOLD_OUT_SUFFIX = ' \u2014 Sold out';

  class VariantMixed extends VariantRadios {
    constructor() {
      super();
      this.onExternalVariantChange = this.onExternalVariantChange.bind(this);
      document.addEventListener('variant:change', this.onExternalVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onExternalVariantChange);
    }

    groups() {
      return Array.from(this.querySelectorAll('.variant-input-wrapper'));
    }

    // Read the current value of every option group, whether it is a <select> or a radio set.
    updateOptions() {
      this.options = this.groups().map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) => radio.checked);
        return checked ? checked.value : null;
      });
    }

    // The theme's availability tree handles radio groups; selects are handled by syncSelects().
    enableVariantOption(group, obj) {
      if (group.querySelector('select')) return;
      super.enableVariantOption(group, obj);
    }

    disableVariantGroup(group) {
      if (group.querySelector('select')) return;
      super.disableVariantGroup(group);
    }

    setAvailability() {
      super.setAvailability();
      this.syncSelects();
    }

    setAvailabilityInUnavailable() {
      super.setAvailabilityInUnavailable();
      this.syncSelects();
    }

    // Disable option values that do not exist given the selections made in earlier option groups,
    // label sold-out values, and auto-select the first valid value if the current one is invalid.
    syncSelects() {
      const variants = this.getVariantData() || [];
      let changedSelect = null;

      this.groups().forEach((group, index) => {
        const select = group.querySelector('select');
        if (!select) return;
        const prior = this.options.slice(0, index);

        Array.from(select.options).forEach((option) => {
          const matches = variants.filter((variant) =>
            variant.options[index] === option.value &&
            prior.every((value, i) => value === null || variant.options[i] === value)
          );
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);

          if (option.dataset.baseText === undefined) option.dataset.baseText = option.textContent.trim();
          option.disabled = !exists;
          option.textContent = option.dataset.baseText + (exists && !available ? SOLD_OUT_SUFFIX : '');
        });

        const current = select.options[select.selectedIndex];
        if (current && current.disabled) {
          const firstValid = Array.from(select.options).find((option) => !option.disabled);
          if (firstValid) {
            select.value = firstValid.value;
            changedSelect = select;
          }
        }
      });

      if (changedSelect) {
        // Re-run the theme's variant change flow with the corrected size.
        changedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Keep this picker in sync when the desktop sticky cart (<variant-selects>) changes the variant.
    onExternalVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !variant.options) return;
      if (this.currentVariant && this.currentVariant.id === variant.id) return;

      let changed = false;
      this.groups().forEach((group, index) => {
        const value = variant.options[index];
        const select = group.querySelector('select');
        if (select) {
          if (select.value !== value) {
            select.value = value;
            changed = true;
          }
          return;
        }
        group.querySelectorAll('input[type="radio"]').forEach((radio) => {
          const shouldCheck = radio.value === value;
          if (shouldCheck && !radio.checked) changed = true;
          radio.checked = shouldCheck;
        });
      });

      if (changed) {
        this.updateOptions();
        this.updateMasterId();
        this.setAvailability();
      }
    }
  }

  customElements.define('variant-mixed', VariantMixed);
})();
